import { SSMLTag, ssmlTagList, SSMLTagsColors } from "shared/constants";
import { Editor as EditorRef } from "@tinymce/tinymce-react";

interface HTMLElementWithEditor extends HTMLElement {
  editor?: EditorRef["editor"];
}

const getAttributeMapping = (attributes: string[], values: string[]) => {
  return attributes
    .map((attr, index) => {
      if (!values[index]) return "";
      return `${attr}='${values[index]}'`;
    })
    .filter(Boolean)
    .join(" ");
};

const processProsodyAttributes = (dataSSML: string) => {
  const attributes: Record<string, string> = {};

  // First split by comma, but handle special case for contour values
  let currentEntry = "";
  let inContourValue = false;

  for (let i = 0; i < dataSSML.length; i++) {
    const char = dataSSML[i];

    if (char === "(" && !inContourValue) {
      inContourValue = true;
    } else if (char === ")") {
      inContourValue = false;
    }

    if (char === "," && !inContourValue) {
      processEntry(currentEntry.trim(), attributes);
      currentEntry = "";
    } else {
      currentEntry += char;
    }
  }

  if (currentEntry.trim()) {
    processEntry(currentEntry.trim(), attributes);
  }

  if (Object.keys(attributes).length === 0) return null;

  const attributeStr = Object.entries(attributes)
    .map(([attr, value]) => `${attr}="${value}"`)
    .join(" ");

  return {
    attributes: attributeStr,
    color: SSMLTagsColors.ProsodyTag,
  };
};

const processEntry = (entry: string, attributes: Record<string, string>) => {
  const [tagWithId, value] = entry.split("=");
  if (!tagWithId || !value) return;

  const [tag] = tagWithId.split("-");
  if (["volume", "rate", "pitch", "duration", "contour"].includes(tag)) {
    // Remove any surrounding quotes if they exist
    const cleanValue = value.replace(/^["']|["']$/g, "");
    attributes[tag] = cleanValue;
  }
};

const escapeRegExp = (string: string) => {
  return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
};

export const removeSSMLTag = (
  editor: EditorRef["editor"],
  tagType: SSMLTag,
  activeSSMLTags: string[]
): void => {
  if (!editor) return;

  const removeSSMLTagFromNode = (node: Element) => {
    const dataSSML = node.getAttribute("data-ssml");
    if (dataSSML) {
      const activeTag = activeSSMLTags.find((tag) =>
        tag.startsWith(`${tagType}-`)
      );
      if (activeTag && dataSSML.includes(activeTag)) {
        const [tagName, tagValue] = activeTag.split("=");

        // Escape special characters in both tagName and tagValue
        const escapedTagName = escapeRegExp(tagName);
        const escapedTagValue = escapeRegExp(tagValue);

        // Create regex with escaped values
        const regex = new RegExp(`${escapedTagName}=${escapedTagValue},?`);
        const updatedDataSSML = dataSSML
          .replace(regex, "")
          .replace(/^,|,$/g, "");

        if (tagType === SSMLTag.Break) {
          // For break tags, remove the entire node
          editor.dom.remove(node);
        } else {
          // Check if the node has nested SSML tags
          const nestedTags = node.querySelectorAll("span[data-ssml]");
          const hasNestedTags = nestedTags.length > 0;

          const fragment = document.createDocumentFragment();

          // Process child nodes carefully to preserve all voice tags and other nested tags
          Array.from(node.childNodes).forEach((child) => {
            if (
              child.nodeType === Node.ELEMENT_NODE &&
              (child as Element).hasAttribute("only-styling")
            ) {
              return;
            }
            fragment.appendChild(child.cloneNode(true));
          });

          // Replace the current tag node with all its preserved content
          node.parentNode?.replaceChild(fragment, node);
          if (updatedDataSSML && hasNestedTags) {
            editor.selection.select(fragment);
            const reselectedNode = editor.selection.getNode();
            // Preserve nested tags
            const preservedTags =
              reselectedNode.querySelectorAll("span[data-ssml]");
            preservedTags.forEach((tag) => {
              if (tag.getAttribute("data-ssml")) {
                // Keep it as it is
              }
            });
          }
        }
        return true;
      }
    }
    return false;
  };

  const traverseAndRemove = (node: Node): boolean => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as Element;
      if (removeSSMLTagFromNode(element)) {
        return true;
      }
    }

    const childNodes = Array.from(node.childNodes);
    for (let i = 0; i < childNodes.length; i++) {
      if (traverseAndRemove(childNodes[i])) {
        return true;
      }
    }

    return false;
  };

  traverseAndRemove(editor.getBody());
  editor.fire("change");
};

const wrapSSMLTag = (element: HTMLElementWithEditor) => {
  while (element.querySelector("span[only-styling]")) {
    const spanChild = element.querySelector("span[only-styling]");
    spanChild?.parentNode?.removeChild(spanChild);
  }

  const processSpansInOrder = () => {
    const spans = Array.from(element.querySelectorAll("span[data-ssml]"));
    spans.sort((a, b) => {
      const depthA = getNodeDepth(a);
      const depthB = getNodeDepth(b);
      return depthB - depthA;
    });

    spans.forEach(processSpan);
  };

  const getNodeDepth = (node: Element): number => {
    let depth = 0;
    let current = node;
    while (current.parentElement) {
      depth++;
      current = current.parentElement;
    }
    return depth;
  };

  const processSpan = (span: Element) => {
    const ssmlValue = span.getAttribute("data-ssml");
    if (!ssmlValue) return;

    const innerContent = span.innerHTML;
    // Special handling for the Phoneme tag
    if (ssmlValue.startsWith(`${SSMLTag.Phoneme}`)) {
      // Find the index of the first comma which separates alphabet and phoneme value
      const commaIndex = ssmlValue.indexOf(",");
      if (commaIndex !== -1) {
        const typeWithId = ssmlValue.substring(0, ssmlValue.indexOf("="));
        const alphabetValue = ssmlValue
          .substring(ssmlValue.indexOf("=") + 1, commaIndex)
          .trim();
        const phonemeValue = ssmlValue.substring(commaIndex + 1).trim();

        const [type] = typeWithId.split("-");
        const button = ssmlTagList.find((b) => b.tag.toLowerCase() === type);

        if (button) {
          const { name, attribute, color } = button;
          const newElement = document.createElement("span");
          newElement.setAttribute("style", `color:${color};`);
          const attributeStr = `${attribute[0]}='${alphabetValue}' ${attribute[1]}='${phonemeValue}'`;

          const customInnerHtml = `&lt;${name} ${attributeStr}&gt;${innerContent}&lt;/${name}&gt;`;
          newElement.innerHTML = customInnerHtml;
          span.parentNode?.replaceChild(newElement, span);
          return;
        }
      }
    }

    // Special handling for say-as tag
    if (ssmlValue.includes("say-as-")) {
      const ssmlValues = ssmlValue.split("=")[1];

      if (ssmlValues) {
        const values = ssmlValues.split(",").map((v) => v.trim());

        const button = ssmlTagList.find(
          (b) =>
            b.tag === SSMLTag.SayAs ||
            b.tag.toLowerCase() === "say-as" ||
            b.name?.toLowerCase() === "say-as"
        );

        const color = button ? button.color : SSMLTagsColors.SayAsTag;
        const name = button ? button.name : "say-as";

        if (values.length >= 1) {
          const newElement = document.createElement("span");
          newElement.setAttribute("style", `color:${color};`);
          newElement.setAttribute("data-ssml", ssmlValue);

          let attributeStr = `interpret-as='${values[0]}'`;

          // Only add language if not empty and not 'automatic'
          if (values[1] && values[1] !== "") {
            attributeStr += ` language='${values[1]}'`;
          }

          if (values[2] && values[2] !== "") {
            attributeStr += ` format='${values[2]}'`;
          }

          if (values[3] && values[3] !== "") {
            attributeStr += ` detail='${values[3]}'`;
          }

          const customInnerHtml = `&lt;${name} ${attributeStr}&gt;${innerContent}&lt;/${name}&gt;`;
          newElement.innerHTML = customInnerHtml;
          span.parentNode?.replaceChild(newElement, span);
          return;
        }
      }
    }

    const prosodyResult = processProsodyAttributes(ssmlValue);
    if (prosodyResult) {
      const newElement = document.createElement("span");
      newElement.setAttribute("style", `color:${prosodyResult.color};`);
      const customInnerHtml = `&lt;prosody ${prosodyResult.attributes}&gt;${innerContent}&lt;/prosody&gt;`;
      newElement.innerHTML = customInnerHtml;
      span.parentNode?.replaceChild(newElement, span);
      return;
    }
    const [typeWithId, ssmlValues] = ssmlValue.split("=");
    const [type] = typeWithId.split("-");
    const values = ssmlValues.split(", ");

    const button = ssmlTagList.find((b) => b.tag.toLowerCase() === type);
    if (button) {
      const { name, tag, attribute, color } = button;
      const isBreakTag = tag === SSMLTag.Break;

      const newElement = document.createElement("span");
      newElement.setAttribute("style", `color:${color};`);

      const attributeMapping = getAttributeMapping(attribute, values);

      const customInnerHtml = isBreakTag
        ? `&lt;${name} ${attributeMapping} /&gt;`
        : `&lt;${name} ${attributeMapping}&gt;${innerContent}&lt;/${name}&gt;`;

      newElement.innerHTML = customInnerHtml;
      span.parentNode?.replaceChild(newElement, span);
    }
  };

  processSpansInOrder();
};

//This function is used to fix the color continuation issue
export const insertFormatBreaker = (element: HTMLElement) => {
  const coloredSpans = element.querySelectorAll('span[style*="color"]');

  coloredSpans.forEach((span) => {
    const nextSibling = span.nextSibling;

    if (!nextSibling || nextSibling.nodeType === 3) {
      const breaker = document.createElement("span");
      breaker.setAttribute("style", "color: rgba(0, 0, 0, 0.87);");
      breaker.setAttribute("only-styling", "true");
      breaker.innerHTML = "&#8203;";

      if (span.nextSibling) {
        span.parentNode?.insertBefore(breaker, span.nextSibling);
      } else {
        span.parentNode?.appendChild(breaker);
      }
    }
  });
};

export function transformToSSML(
  content: string,
  shouldHaveStyling: boolean
): string {
  const decoded = content
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&");

  const alreadySSML =
    /<(say-as|voice|prosody|emphasis|lang|break|phoneme|style)\b/i.test(
      decoded
    );

  if (alreadySSML) {
    return decoded;
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(content, "text/html");
  wrapSSMLTag(doc.body);
  insertFormatBreaker(doc.body);
  let result = doc.body.innerHTML;

  if (!shouldHaveStyling) {
    result = result
      .replace(/<br\s*\/?>/gi, "")
      .replace(/<tt>/gi, "")
      .replace(/<\/tt>/gi, "")
      .replace(/<span[^>]*>/g, "")
      .replace(/<\/span>/g, "")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">");
  }

  return result;
}

export function transformToValidSSML(text: string): string {
  let ssml = text.trim();

  ssml = fixUnclosedTags(ssml);

  if (!ssml.startsWith("<speak>")) {
    ssml = `<speak>${ssml}`;
  }
  if (!ssml.endsWith("</speak>")) {
    ssml = `${ssml}</speak>`;
  }
  return validateAndFixSSMLStructure(ssml);
}

export function getWrappedInput(input: string): string {
  const cleanedInput = input.trim().replace(/&nbsp;/g, " ");

  let ssml = cleanedInput;

  if (!ssml.startsWith("<speak>")) {
    ssml = `<speak>${ssml}`;
  }
  if (!ssml.endsWith("</speak>")) {
    ssml = `${ssml}</speak>`;
  }

  return ssml;
}

function fixUnclosedTags(text: string): string {
  let result = text;

  // Match potential unclosed tags
  const unclosedTagsRegex =
    /<([a-zA-Z][a-zA-Z0-9]*)(?:\s+[^>]*)?(?!.*<\/\1>).*?>/g;
  const matches = result.match(unclosedTagsRegex);

  if (matches) {
    // Process tags in reverse order to handle nesting correctly
    matches.reverse().forEach((match) => {
      const tagNameMatch = /<([a-zA-Z][a-zA-Z0-9]*)/.exec(match);
      if (tagNameMatch && tagNameMatch[1]) {
        const tagName = tagNameMatch[1];

        // Add closing tag before the end speak tag or at the end
        const closeTagPos = result.lastIndexOf("</speak>");
        if (closeTagPos > -1) {
          result =
            result.slice(0, closeTagPos) +
            `</${tagName}>` +
            result.slice(closeTagPos);
        } else {
          result += `</${tagName}>`;
        }
      }
    });
  }

  return result;
}

function validateAndFixSSMLStructure(ssml: string): string {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(ssml, "text/xml");
    const parseError = doc.querySelector("parsererror");
    if (parseError) {
      return simplifySSML(ssml);
    }

    return ssml;
  } catch (error) {
    return simplifySSML(ssml);
  }
}

function simplifySSML(ssml: string): string {
  // Remove prosody tags entirely but keep their content
  let simplified = ssml
    .replace(/<prosody[^>]*>/g, "")
    .replace(/<\/prosody>/g, "");

  // Remove phoneme tags but keep their content
  simplified = simplified
    .replace(/<phoneme[^>]*>/g, "")
    .replace(/<\/phoneme>/g, "");

  if (!simplified.includes("<speak>")) {
    simplified = `<speak>${simplified}</speak>`;
  } else if (!simplified.endsWith("</speak>")) {
    simplified += "</speak>";
  }

  return simplified;
}

export function validateSSML(ssml: string): {
  isValid: boolean;
  errorMessage?: string;
} {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(ssml, "text/xml");

    // Check for parsing errors
    const parseError = doc.querySelector("parsererror");
    if (parseError) {
      return {
        isValid: false,
        errorMessage:
          parseError.textContent || "Invalid SSML - XML parsing error",
      };
    }

    return { isValid: true };
  } catch (e) {
    return {
      isValid: false,
      errorMessage:
        e instanceof Error ? e.message : "Unknown SSML validation error",
    };
  }
}

export function getSanitizedInput(input: string): string {
  const cleanedInput = input.trim().replace(/&nbsp;/g, " ");

  let wrappedSSML = cleanedInput;
  if (!wrappedSSML.startsWith("<speak>")) {
    wrappedSSML = `<speak>${wrappedSSML}`;
  }
  if (!wrappedSSML.endsWith("</speak>")) {
    wrappedSSML = `${wrappedSSML}</speak>`;
  }

  // Check if the wrapped SSML is already valid
  const initialValidation = validateSSML(wrappedSSML);
  if (initialValidation.isValid) {
    return wrappedSSML;
  }

  return transformToValidSSML(cleanedInput);
}

export function cleanupSSML(ssmlText: string): string {
  let cleaned = ssmlText;

  cleaned = cleaned.replace(
    /<(\w+)(?![^>]*(?:name|language|voice|lang))[^>]*><\/\1>/g,
    ""
  );

  const openTags: string[] = [];
  const tagRegex = /<\/?(\w+)[^>]*>/g;
  let match;

  while ((match = tagRegex.exec(cleaned)) !== null) {
    const isClosing = match[0].startsWith("</");
    const tagName = match[1];

    if (isClosing) {
      const lastOpenIndex = openTags.lastIndexOf(tagName);
      if (lastOpenIndex !== -1) {
        openTags.splice(lastOpenIndex, 1);
      }
    } else if (!match[0].endsWith("/>")) {
      openTags.push(tagName);
    }
  }

  // Close any remaining open tags
  for (let i = openTags.length - 1; i >= 0; i--) {
    cleaned += `</${openTags[i]}>`;
  }

  return cleaned;
}

export function sanitizePhInSsml(ssml: string): string {
  const PH_STRIP_REGEX = /[\\`~"]/g;

  return ssml.replace(
    /<phoneme\b([^>]*)>/gi,
    (fullTag: string, attrs: string): string => {
      const newAttrs = attrs.replace(
        /\bph\s*=\s*("([^"]*)"|'([^']*)')/i,
        (
          match: string,
          _fullVal: string,
          doubleVal: string | undefined,
          singleVal: string | undefined
        ): string => {
          const rawVal =
            doubleVal !== undefined ? doubleVal : (singleVal ?? "");
          const cleaned = rawVal.replace(PH_STRIP_REGEX, "");
          return doubleVal !== undefined
            ? ` ph="${cleaned}"`
            : ` ph='${cleaned}'`;
        }
      );

      return `<phoneme${newAttrs}>`;
    }
  );
}

export function cleanWordSSML(ssmlContent: string) {
  if (!ssmlContent) {
    return ssmlContent;
  }
  const hasWordTags = /class="?Mso|style="[^"]*\bmso-|<o:p>|<\/?o:/i.test(
    ssmlContent
  );

  if (!hasWordTags) {
    // No Word tags detected, return original content
    return ssmlContent;
  }

  let cleaned = ssmlContent;

  cleaned = cleaned.replace(/<o:p>.*?<\/o:p>/gi, "");
  cleaned = cleaned.replace(/<\/?o:\w+[^>]*>/gi, "");
  cleaned = cleaned.replace(/<p\s+class=["']?Mso\w*["']?[^>]*>/gi, "");
  cleaned = cleaned.replace(/<\/p>/gi, "");
  cleaned = cleaned.replace(/\s*style=["'][^"']*\bmso-[^"']*["']/gi, "");
  cleaned = cleaned.replace(/\s*class=["']?Mso\w*["']?/gi, "");
  cleaned = cleaned.replace(/\s+/g, " ").trim();

  return cleaned;
}

/**
 * Strips all <speak>...</speak> wrapper tags from a string.
 * Handles optional attributes on the opening tag, multiple/sequential
 * speak tags, and leading/trailing whitespace.
 */
export function stripSpeakTags(content: string): string {
  return content
    .replace(/<speak\b[^>]*>/gi, "")
    .replace(/<\/speak>/gi, "")
    .trim();
}
